import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { SERVER_NAME } from './config';
import axios from 'axios';
import useStyles from './sharedStyles'; 
import { 
  Button, 
  TextField, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  IconButton, 
  Checkbox, 
  FormControlLabel, 
  Radio, 
  CircularProgress,
  Grid
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { FaTimes } from 'react-icons/fa';
import { subscribeWithETH, subscribeWithE3D, getCurrentAccount, SubscriptionTier } from './web3/contractInteractions';
import { useAuth } from './auth/AuthContext';

// Animation styles for the popup dialog
const popupStyles = `
  @keyframes fadeIn {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
  }
  
  @keyframes slideUp {
    from { transform: translateY(10px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

const SignupDialog = ({ open, onClose, onSignup, standalone = false, username: usernameFromProps, setUsername, handleLogin, userAddress: userAddressFromProps, userID: userIDFromProps, isAccountCreation = false }) => {
  console.log('============ SIGNUP DIALOG MOUNT ============', { isAccountCreation });

  const rateLimitTiers = [
    { label: 'Free', dailyLimit: '100 requests/day', interval: '5 seconds between requests' },
    { label: 'Premium', dailyLimit: '1000 requests/day', interval: '1 second between requests' },
    { label: 'Enterprise', dailyLimit: '100000 requests/day', interval: '10 milliseconds between requests' },
  ];
  
  // Store original alert function at component level
  const originalAlert = useRef(null);
  const lastAddressPersistedRef = useRef('');
  
  // Use an effect to handle alert suppression/restoration
  useEffect(() => {
    // Save the original alert function when component mounts
    originalAlert.current = window.alert;
    
    // Set up a mutation observer to catch and remove alert dialogs
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.addedNodes && mutation.addedNodes.length) {
          for (const node of mutation.addedNodes) {
            // Check if this might be an alert dialog
            if (node.nodeType === 1 && (
                (node.tagName === 'DIALOG' || node.tagName === 'DIV') && 
                node.textContent && 
                node.textContent.includes('Account created'))) {
              
              console.log('Found alert dialog with "Account created" text - removing it');
              
              // If it's a Chrome dialog that appears after signup
              if (node.querySelector && node.querySelector('button')) {
                // Find the OK button and click it to dismiss dialog
                const buttons = node.querySelectorAll('button');
                for (const button of buttons) {
                  if (button.textContent === 'OK') {
                    console.log('Clicking OK button to dismiss dialog');
                    button.click();
                    break;
                  }
                }
              }
              
              // In some cases we might need to remove the node
              if (node.parentNode) {
                node.parentNode.removeChild(node);
              }
            }
          }
        }
      }
    });
    
    // Start observing the document body for DOM changes
    observer.observe(document.body, { childList: true, subtree: true });
    
    // Clean up when component unmounts
    return () => {
      // Stop the observer
      observer.disconnect();
      
      // Restore the original alert if it was replaced
      if (window.alert !== originalAlert.current && originalAlert.current) {
        console.log('Restoring alert on component unmount');
        window.alert = originalAlert.current;
      }
    };
  }, []);
  
  // Use auth context for authentication state
  // Need to destructure login here at the component level
  const { isAuthenticated: contextAuthenticated, login } = useAuth();
  
  // DIRECT CHECK: Read from localStorage for authentication status
  // This ensures we always have the most up-to-date auth state
  const localStorageAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
  
  // Use the most permissive authentication state - if either says we're logged in, we're logged in
  // This prevents the "appears logged out" issue when navigating
  const isAuthenticated = contextAuthenticated || localStorageAuthenticated;
  
  console.log('Auth state check - Context vs LocalStorage:', {
    contextAuthenticated,
    localStorageAuthenticated,
    effectiveAuthenticated: isAuthenticated
  });
  
  // Check for pending signup email from login dialog first, then use other fallbacks
  const pendingEmail = localStorage.getItem('pendingSignupEmail');
  const initialUsername = usernameFromProps || pendingEmail || localStorage.getItem('username') || '';
  
  // Clear the pending email after using it
  if (pendingEmail) {
    localStorage.removeItem('pendingSignupEmail');
  }
  
  // Add local state for email/username to update without reload
  const [localUsername, setLocalUsername] = useState(initialUsername);
  const effectiveUsername = localUsername; // Use local state for the component
  const effectiveUserID = userIDFromProps || localStorage.getItem('userID') || '';
  const effectiveUserAddress = userAddressFromProps || localStorage.getItem('userAddress') || '';
  
  // Track if component is ready to render with proper auth state
  const [isReady, setIsReady] = useState(false);

  const [mongoUserAddress, setMongoUserAddress] = useState('');
  const [mongoUserRoles, setMongoUserRoles] = useState([]);
  const [showLogin, setShowLogin] = useState(false);
  
  // Add animation styles to head when component mounts
  useEffect(() => {
    if (isAccountCreation) {
      // Create a style element and add it to the head
      const styleEl = document.createElement('style');
      styleEl.id = 'signup-dialog-animations';
      styleEl.innerHTML = popupStyles;
      document.head.appendChild(styleEl);
      
      // Clean up on unmount
      return () => {
        const existingStyle = document.getElementById('signup-dialog-animations');
        if (existingStyle) document.head.removeChild(existingStyle);
      };
    }
  }, [isAccountCreation]);
  
  // Force auth context to synchronize with localStorage on component mount
  // This fixes the "appears logged out" issue by ensuring context matches localStorage
  const syncAuthContext = async () => {
    // Read directly from localStorage
    const storedIsAuth = localStorage.getItem('isAuthenticated') === 'true';
    const storedUsername = localStorage.getItem('username');
    const storedUserID = localStorage.getItem('userID');
    const storedUserAddress = localStorage.getItem('userAddress');
    
    // If localStorage shows logged in but context doesn't, force a login sync
    if (storedIsAuth && !contextAuthenticated && storedUserID) {
      console.log('🔄 Forcing auth context sync from localStorage');
      login({
        username: storedUsername || '',
        userID: storedUserID || '',
        userAddress: storedUserAddress || ''
      });
    }
    
    // Server check to verify auth status
    try {
      const response = await axios.get(`${SERVER_NAME}/auth/status`, {
        withCredentials: true,
        timeout: 8000,
      });
      console.log('Server auth verification:', response.data);
      
      if (response.data.isAuthenticated && !isAuthenticated) {
        console.log('🔄 Forcing auth context sync from server');
        login({
          username: response.data.username || '',
          userID: response.data.userID || response.data.userId || '',
          userAddress: response.data.userAddress || ''
        });
      }
    } catch (error) {
      console.error('Error verifying auth with server:', error);
    } finally {
      // Mark component as ready regardless of auth status
      setIsReady(true);
    }
  };
  
  // Ensure auth state is properly loaded before rendering UI
  useEffect(() => {
    console.log('SignupDialog mounted with auth state:', { 
      contextAuthenticated,
      localStorageAuthenticated,
      isAuthenticated,
      effectiveUsername,
      effectiveUserID,
      effectiveUserAddress
    });
    
    // Run the sync on mount
    syncAuthContext();
  }, []);

  // Handle the return from Stripe Checkout on the standalone /signup page --
  // the webhook may not have landed yet, so claim the session directly as a
  // fallback (same "claim on browser return" pattern as the credit-pack flow).
  useEffect(() => {
    if (!standalone) return;
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('stripe_subscription_session_id');
    const cancelled = params.get('stripe_subscription_cancelled');

    if (cancelled) {
      window.history.replaceState({}, '', '/signup');
      return;
    }
    if (!sid) return;

    setTransactionStatus('Confirming your subscription...');
    axios.get(`${SERVER_NAME}/api/subscription/stripe/session/${encodeURIComponent(sid)}/result`, {
      withCredentials: true,
      timeout: 15000,
    })
      .then(() => {
        setTransactionStatus(null);
        window.history.replaceState({}, '', '/signup');
        setSubscriptionRefreshNonce((n) => n + 1);
      })
      .catch((e) => {
        console.error('Error confirming Stripe subscription:', e);
        setTransactionStatus('Could not confirm subscription -- it may still be processing.');
      });
  }, [standalone]);

  // Track component lifecycle for debugging
  const renderCountRef = useRef(0);
  useEffect(() => {
    renderCountRef.current += 1;
    console.log(`SignupDialog render #${renderCountRef.current}`);
    
    // CRITICAL: Force re-check auth status on mount to prevent stale state issues
    // This runs only once when the component mounts
    if (renderCountRef.current === 1) {
      // Read from localStorage directly to ensure we have the latest values
      const storedIsAuth = localStorage.getItem('isAuthenticated') === 'true';
      const storedUserID = localStorage.getItem('userID');
      
      console.log('SignupDialog mount sync check:', { 
        contextAuthenticated: isAuthenticated,
        storedAuthenticated: storedIsAuth,
        storedUserID
      });
      
      // If there's a mismatch between context and localStorage, force a check
      if (storedIsAuth !== isAuthenticated) {
        console.warn('Auth state mismatch detected! Local storage shows:', storedIsAuth, 'Context shows:', isAuthenticated);
      }
    }
  });

  // Clear any stale subscription details when user is not authenticated
  // Effect to sync with authentication state changes
  useEffect(() => {
    console.log('Auth state change detected in SignupDialog', { isAuthenticated, isAccountCreation });
    
    // When authentication status changes, update payment method accordingly
    if (isAccountCreation) {
      // Always default to free for account creation
      setPaymentMethod('free');
    } else if (isAuthenticated) {
      // For authenticated users, default to card -- lowest friction, no wallet needed
      setPaymentMethod('card');
    } else {
      // For non-authenticated users, default to free
      setPaymentMethod('free');
      
      // Also clear subscription details when logging out
      setSubscriptionDetails(null);
    }
  }, [isAuthenticated, isAccountCreation]);
  
  // Clear subscription details when user address changes or on logout
  useEffect(() => {
    if (!isAuthenticated) {
      setSubscriptionDetails(null);
      setMongoUserAddress('');
      setMongoUserRoles([]);
    }
  }, [isAuthenticated]);

  // Basic state for form fields
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(true);
  // Initialize payment method based on auth context state and isAccountCreation flag
  const [paymentMethod, setPaymentMethod] = useState(isAccountCreation ? 'free' : (isAuthenticated ? 'card' : 'free')); // Always free for account creation, card for authenticated users (no wallet needed), free for non-auth users
  const [subscriptionInterval, setSubscriptionInterval] = useState('year'); // 'month' | 'year'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [transactionStatus, setTransactionStatus] = useState(null);
  const [walletConnected, setWalletConnected] = useState(false);
  const [subscriptionRefreshNonce, setSubscriptionRefreshNonce] = useState(0);
  
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [subscriptionDetailsLoading, setSubscriptionDetailsLoading] = useState(false);
  const [subscriptionDetailsLoaded, setSubscriptionDetailsLoaded] = useState(false);

  const [walletSubscriptionDetails, setWalletSubscriptionDetails] = useState(null);
  const [walletSubscriptionLoading, setWalletSubscriptionLoading] = useState(false);
  const [walletSubscriptionLoaded, setWalletSubscriptionLoaded] = useState(false);

  // Email validation check
  const [usernameError, setUsernameError] = useState(false);
  // Password matching check
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);
  
  // Check for wallet connection and subscription details
  useEffect(() => {
    // Only fetch details if component is mounted and authentication is ready
    if (!isReady) return;

    // Account creation (email/password) should not require a wallet or MetaMask.
    // Skip all wallet/subscription probing to avoid noisy "No web3 provider" errors.
    if (isAccountCreation) {
      setWalletConnected(false);
      setSubscriptionDetails(null);
      setSubscriptionDetailsLoading(false);
      setSubscriptionDetailsLoaded(false);
      setWalletSubscriptionDetails(null);
      setWalletSubscriptionLoading(false);
      setWalletSubscriptionLoaded(false);
      return;
    }
    
    const checkWalletConnection = async () => {
      try {
        // First check localStorage for stored wallet
        const storedAddress = localStorage.getItem('userAddress');
        
        // Import dynamically to reduce bundle size
        const { getCurrentAccount } = await import('./web3/contractInteractions');
        const accounts = await getCurrentAccount(false); // false = don't prompt
        
        if (accounts && accounts.length > 0) {
          console.log('Active wallet detected:', accounts[0]);
          try {
            const addr = String(accounts[0] || '').trim();
            if (addr) localStorage.setItem('userAddress', addr);
            if (isAuthenticated && addr && lastAddressPersistedRef.current !== addr) {
              await axios.post(`${SERVER_NAME}/user/address`, { address: addr }, { withCredentials: true, timeout: 12000 });
              lastAddressPersistedRef.current = addr;
            }
          } catch (e) {
            console.error('Error storing wallet address:', e);
          }
          setWalletConnected(true);
        } else if (storedAddress) {
          console.log('Using stored wallet address:', storedAddress);
          try {
            if (isAuthenticated && storedAddress && lastAddressPersistedRef.current !== storedAddress) {
              await axios.post(`${SERVER_NAME}/user/address`, { address: storedAddress }, { withCredentials: true, timeout: 12000 });
              lastAddressPersistedRef.current = storedAddress;
            }
          } catch (e) {
            console.error('Error storing wallet address:', e);
          }
          setWalletConnected(true);
        } else {
          setWalletConnected(false);
        }
      } catch (error) {
        console.error("Wallet check error:", error);
        // Don't set false if we have a stored address
        if (!localStorage.getItem('userAddress')) {
          setWalletConnected(false);
        }
      }
    };
    
    const withTimeout = async (p, ms) => {
      return await Promise.race([
        p,
        new Promise((_, reject) => setTimeout(() => reject(new Error('Subscription details timeout')), ms)),
      ]);
    };

    const fetchMongoSubscriptionDetails = async () => {
      const isAuthenticatedAnywhere = isAuthenticated || localStorage.getItem('isAuthenticated') === 'true';
      if (!isAuthenticatedAnywhere) {
        setSubscriptionDetails(null);
        setSubscriptionDetailsLoading(false);
        setSubscriptionDetailsLoaded(true);
        return;
      }

      setSubscriptionDetailsLoading(true);
      setSubscriptionDetailsLoaded(false);
      try {
        const resp = await withTimeout(
          axios.get(`${SERVER_NAME}/subscription`, { withCredentials: true, timeout: 12000 }),
          13000
        );
        const addr = resp && resp.data && resp.data.address ? String(resp.data.address) : '';
        const roles = resp && resp.data && Array.isArray(resp.data.roles) ? resp.data.roles : [];
        setMongoUserAddress(addr);
        setMongoUserRoles(roles);
        const sub = resp && resp.data && resp.data.subscription ? resp.data.subscription : null;
        const normalized = sub ? {
          tier: Number(sub.tier || 0),
          expiration: sub.expiration ? new Date(sub.expiration) : null,
          remainingMints: sub.remainingMints,
          isActive: (sub.status === 'active'),
          paidWithE3D: !!sub.paidWithE3D,
          paymentMethod: sub.paymentMethod || '',
          source: sub.source || 'mongo'
        } : null;
        setSubscriptionDetails(normalized);
      } catch (e) {
        console.error('Error fetching Mongo subscription details:', e);
        setSubscriptionDetails(null);
        setMongoUserAddress('');
        setMongoUserRoles([]);
      } finally {
        setSubscriptionDetailsLoading(false);
        setSubscriptionDetailsLoaded(true);
      }
    };

    const fetchWalletSubscriptionDetails = async () => {
      const addressToUse = effectiveUserAddress || localStorage.getItem('userAddress');
      if (!walletConnected || !addressToUse) {
        setWalletSubscriptionDetails(null);
        setWalletSubscriptionLoading(false);
        setWalletSubscriptionLoaded(true);
        return;
      }
      setWalletSubscriptionLoading(true);
      setWalletSubscriptionLoaded(false);
      try {
        const { getSubscriptionDetails } = await import('./web3/contractInteractions');
        const details = await withTimeout(getSubscriptionDetails(addressToUse), 12000);
        setWalletSubscriptionDetails(details);
      } catch (e) {
        console.error('Error fetching wallet subscription details:', e);
        setWalletSubscriptionDetails(null);
      } finally {
        setWalletSubscriptionLoading(false);
        setWalletSubscriptionLoaded(true);
      }
    };
    
    checkWalletConnection();
    fetchMongoSubscriptionDetails();
    fetchWalletSubscriptionDetails();
  }, [isAuthenticated, effectiveUserAddress, isReady, isAccountCreation, walletConnected, subscriptionRefreshNonce]);

  
  // Validate email as user types
  useEffect(() => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setUsernameError(effectiveUsername.length > 0 && !emailPattern.test(effectiveUsername));
  }, [effectiveUsername]);
  
  // Validate password match as user types
  useEffect(() => {
    setConfirmPasswordError(confirmPassword.length > 0 && password !== confirmPassword);
  }, [password, confirmPassword]);
  
  const handlePaymentMethodChange = (event) => {
    // For authenticated users upgrading, we only allow premium options
    if (isAuthenticated && event.target.value === 'free') {
      return; // Don't allow changing to free for upgrade path
    }
    
    // If selecting premium option but not logged in, offer to redirect to login
    if (event.target.value !== 'free' && !isAuthenticated) {
      const wantToLogin = window.confirm("Premium subscriptions require an account. Would you like to log in first?");
      if (wantToLogin) {
        // Redirect to login page
        if (standalone) {
          window.location.href = '/login';
          return;
        } else if (handleLogin) {
          // For dialog mode, call the provided handleLogin function
          handleLogin();
          return;
        }
      }
    }
    
    setPaymentMethod(event.target.value);
  };

  // Custom hook to get window width for responsive design
  const useWindowSize = () => {
    const [size, setSize] = useState({ width: window.innerWidth, height: window.innerHeight });
    
    useEffect(() => {
      const handleResize = () => {
        setSize({ width: window.innerWidth, height: window.innerHeight });
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    return size;
  };

  const { width } = useWindowSize();
  const isMobile = width < 768; // Mobile breakpoint
  
  // If this component is not in standalone mode and not open as a dialog, don't render anything
  if (!standalone && !open) return null;
  
  // Use React Router's navigate function when available
  const navigateToHome = () => {
    if (typeof window !== 'undefined') {
      // For standalone mode, use window.location to navigate back to home
      if (standalone) {
        window.location.href = '/';
        return;
      }
      
      // For dialog mode, use the provided onClose handler
      if (onClose) {
        onClose();
      }
    }
  };

  // Connect to wallet
  const connectWallet = async () => {
    try {
      await getCurrentAccount();
      setWalletConnected(true);
    } catch (error) {
      console.error("Error connecting wallet:", error);
      alert("Could not connect to wallet. Please make sure MetaMask is installed and unlocked.");
    }
  };
  
  // Handle blockchain subscription
  const handleBlockchainSubscription = async () => {
    setTransactionStatus("Initiating blockchain transaction...");
    
    try {
      const subscriptionTier = subscriptionInterval === 'year' ? SubscriptionTier.Yearly : SubscriptionTier.Monthly;

      if (paymentMethod === 'eth') {
        await subscribeWithETH(subscriptionTier);
      } else if (paymentMethod === 'e3d') {
        await subscribeWithE3D(subscriptionTier);
      }
      
      try {
        const addr = effectiveUserAddress || localStorage.getItem('userAddress');
        if (addr) {
          await axios.post(`${SERVER_NAME}/user/address`, { address: addr }, { withCredentials: true, timeout: 12000 });
          await axios.post(`${SERVER_NAME}/subscription/syncOnChain`, { address: addr }, { withCredentials: true, timeout: 20000 });
        }
      } catch (syncErr) {
        console.error('Error syncing subscription to Mongo after purchase:', syncErr);
      }

      setTransactionStatus("Subscription confirmed! Creating account...");
      return true;
    } catch (error) {
      console.error("Blockchain transaction error:", error);
      setTransactionStatus("Transaction failed. Please try again.");
      return false;
    }
  };

  // Redirect to Stripe-hosted Checkout for a card subscription. Navigation
  // away happens here; the caller has nothing further to do on success.
  const handleCardSubscription = async () => {
    setTransactionStatus("Redirecting to secure checkout...");
    try {
      const response = await axios.post(
        `${SERVER_NAME}/api/subscription/stripe/checkout`,
        { interval: subscriptionInterval },
        { withCredentials: true, timeout: 15000 }
      );
      if (response.data && response.data.url) {
        window.location.href = response.data.url;
        return true;
      }
      throw new Error('No checkout URL returned');
    } catch (error) {
      console.error("Stripe checkout error:", error);
      setTransactionStatus("Could not start checkout. Please try again.");
      return false;
    }
  };

  // Function to temporarily suppress browser alerts
  const suppressAlerts = () => {
    // Store the current alert function if not already stored
    if (!originalAlert.current) {
      originalAlert.current = window.alert;
    }
    
    // Replace with a no-op function that just logs
    window.alert = (message) => {
      console.log('Alert suppressed:', message);
      // Return a fake promise that resolves immediately to handle any code expecting a result
      return Promise.resolve(true);
    };

    // Also try to suppress any window or document-level dialogs
    if (window.HTMLDialogElement) {
      const originalShowModal = window.HTMLDialogElement.prototype.showModal;
      window.HTMLDialogElement.prototype.showModal = function() {
        if (this.id !== 'verify-email-root' && this.textContent?.includes('log in')) {
          console.log('Dialog suppressed:', this.textContent);
          return;
        }
        return originalShowModal.apply(this, arguments);
      };
    }
  };
  
  // Function to restore original alert behavior
  const restoreAlerts = () => {
    if (originalAlert.current) {
      window.alert = originalAlert.current;
      console.log('Original alert behavior restored');
    }
  };
  
  const handleSignupSubmit = async (event) => {
    if (event) {
      event.preventDefault();
    }
    
    setIsSubmitting(true);
    
    // Suppress alerts during signup process
    suppressAlerts();
    
    // Debug check for authentication state right before submission
    console.log('Auth Check Before Signup:', {
      effectiveUserID,
      isAuthenticated,
      mode: isAuthenticated ? 'UPGRADE' : 'CREATE',
      isAccountCreation
    });
    
    // Special validation for account creation mode
    if (isAccountCreation) {
      console.log('Account creation mode - validating input fields');
      
      // Check if passwords are provided
      if (!password) {
        alert("Please enter a password.");
        setIsSubmitting(false);
        return;
      }
      
      // Check if passwords match
      if (password !== confirmPassword) {
        alert("Passwords don't match.");
        setIsSubmitting(false);
        return;
      }
      
      // Email validation regex pattern
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      
      // Check if the email is valid
      if (!emailPattern.test(effectiveUsername)) {
        alert("Please enter a valid email address.");
        setIsSubmitting(false);
        return;
      }
    }
    // For authenticated users, we're just doing an upgrade, so skip email and password validation
    else if (!isAuthenticated) {
      // Check if passwords match
      if (password !== confirmPassword) {
        alert("Passwords don't match.");
        setIsSubmitting(false);
        return;
      }
    
      // Email validation regex pattern
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
      // Check if the email is valid
      if (!emailPattern.test(effectiveUsername)) {
        alert("Please enter a valid email address.");
        setIsSubmitting(false);
        return;
      }
    }
    
    // If in account creation mode, skip premium validation
    if (!isAccountCreation) {
      // If premium option is selected, verify login status first
      if (paymentMethod !== 'free') {
        if (!isAuthenticated) {
          alert("You must be logged in before subscribing to premium features.");
          setIsSubmitting(false);
          return;
        }

        if (paymentMethod === 'card') {
          // Navigates away to Stripe Checkout on success; nothing left to do here.
          await handleCardSubscription();
          setIsSubmitting(false);
          return;
        }

        // eth / e3d: requires a connected wallet
        if (!walletConnected) {
          alert("Please connect your wallet to proceed with premium subscription.");
          setIsSubmitting(false);
          return;
        }

        const success = await handleBlockchainSubscription();
        if (!success) {
          setIsSubmitting(false);
          return;
        }
      }
    }
    
    try {
      // If this is specifically for account creation, make sure we're using the account creation flow
      if (isAccountCreation) {
        if (onSignup) {
          console.log('Using App.js handleSignup function for account creation');
          // We're using the App.js handleSignup function to create the account
          // This will call the /register endpoint and handle login
          try {
            // Create the account but don't log in yet
            await onSignup(effectiveUsername, password, subscribeNewsletter);
            console.log('Account creation successful - awaiting verification');
            
            // Close the signup dialog first before showing verification dialog
            if (onClose) {
              onClose();
            }
            
            // Send email verification code
            try {
              await axios.post(`${SERVER_NAME}/sendEmailCode`, { userId: effectiveUsername });
              console.log('Verification code sent to email');
              
              // Create element to mount the VerifyEmailCodeDialog
              const verifyEmailRoot = document.createElement('div');
              verifyEmailRoot.id = 'verify-email-root';
              document.body.appendChild(verifyEmailRoot);
              
              // Use ReactDOM.render to mount the VerifyEmailCodeDialog
              const { VerifyEmailCodeDialog } = require('./AuthDialogs');
              const ReactDOM = require('react-dom');
              
              // Slight delay to ensure signup dialog is closed first
              setTimeout(() => {
                ReactDOM.render(
                  <VerifyEmailCodeDialog 
                    username={effectiveUsername}
                    // Only complete login process after successful verification
                    onClose={(verified = false) => {
                      ReactDOM.unmountComponentAtNode(verifyEmailRoot);
                      document.body.removeChild(verifyEmailRoot);
                      
                      // Only log the user in if they verified their email
                      if (verified && handleLogin) {
                        console.log('Email verified successfully, logging in user');
                        handleLogin();
                      } else {
                        console.log('Email verification canceled or failed, not logging in');
                      }
                    }} 
                  />,
                  verifyEmailRoot
                );
              }, 100); // Short delay for smooth transition
              
            } catch (error) {
              console.error('Error sending verification code:', error);
              alert('An error occurred while sending verification code. Please try again later.');
              
              // Do NOT log in if verification fails - require verification
            }
          } catch (error) {
            console.error('Error creating account:', error);
            alert('Error creating account. Please try again.');
            setIsSubmitting(false);
          }
          return;
        }
      }

      // Authenticated users only reach this point via a completed eth/e3d
      // blockchain subscription (card already redirected away above, and
      // free isn't selectable while authenticated). handleBlockchainSubscription
      // already ran the on-chain tx and synced Mongo via /subscription/syncOnChain
      // -- there is no separate "upgrade" endpoint to call, just close out.
      if (isAuthenticated) {
        if (onSignup) {
          onSignup(effectiveUsername, password, subscribeNewsletter);
        }
        if (onClose) {
          onClose();
        }
        if (standalone) {
          window.location.href = '/';
        } else {
          // Refresh the subscription-status panel to reflect the new plan.
          setSubscriptionRefreshNonce((n) => n + 1);
        }
        return;
      }

      // Otherwise proceed with the normal (unauthenticated) signup flow
      const signupData = {
        email: effectiveUsername,
        password: password,
        paymentMethod: paymentMethod,
        subscribeNewsletter: subscribeNewsletter
      };

      // Extra precaution: hide any alerts that might appear during this process
      suppressAlerts();

      const response = await axios.post(`${SERVER_NAME}/signup`, signupData);
      console.log('Signup response:', response.data);

      if (response.data.success) {
          // First close the signup dialog immediately (unauthenticated signup path)
          if (onClose) {
            onClose();
          }
          
          // Short timeout to ensure the dialog is closed
          await new Promise(resolve => setTimeout(resolve, 50));
          
          // Remove any alert dialogs that might be showing
          document.querySelectorAll('dialog, .MuiDialog-root').forEach(dialog => {
            if (dialog.textContent && dialog.textContent.toLowerCase().includes('account created')) {
              console.log('Removing a found dialog:', dialog.textContent.substring(0, 30) + '...');
              if (dialog.close) dialog.close(); // For HTML dialog elements
              if (dialog.parentNode) dialog.parentNode.removeChild(dialog); // Force removal
            }
          });
          
          // More aggressive suppression
          suppressAlerts();
          
          // Send email verification code
          try {
            await axios.post(`${SERVER_NAME}/sendEmailCode`, { userId: effectiveUsername });
            console.log('Verification code sent to email');
            
            // Create a fresh element for verification dialog that's sure to be clean
            const existingRoot = document.getElementById('verify-email-root');
            if (existingRoot) {
              document.body.removeChild(existingRoot);
            }
            
            const verifyEmailRoot = document.createElement('div');
            verifyEmailRoot.id = 'verify-email-root';
            verifyEmailRoot.style.position = 'fixed';
            verifyEmailRoot.style.top = '0';
            verifyEmailRoot.style.left = '0';
            verifyEmailRoot.style.width = '100%';
            verifyEmailRoot.style.height = '100%';
            verifyEmailRoot.style.zIndex = '9999'; // Ensure highest z-index
            document.body.appendChild(verifyEmailRoot);
            
            // Import required modules
            const { VerifyEmailCodeDialog } = require('./AuthDialogs');
            const ReactDOM = require('react-dom');
            
            // Store the user info but don't complete login until verification
            const userInfo = {
              username: effectiveUsername,
              userID: response.data.userId || effectiveUsername,
              userAddress: response.data.userAddress || ''
            };
            
            // Render verification dialog immediately
            try {
              ReactDOM.render(
                <VerifyEmailCodeDialog 
                  username={effectiveUsername} 
                  onClose={(verified = false) => {
                    try {
                      ReactDOM.unmountComponentAtNode(verifyEmailRoot);
                      if (verifyEmailRoot.parentNode) {
                        verifyEmailRoot.parentNode.removeChild(verifyEmailRoot);
                      }
                    } catch (cleanupError) {
                      console.error('Error cleaning up verification dialog:', cleanupError);
                    }
                    
                    if (verified) {
                      // Only complete the login after successful verification
                      console.log('Email verified successfully, logging in user');
                      login(userInfo);
                      
                      if (onSignup) {
                        onSignup(effectiveUsername, password, subscribeNewsletter);
                      }
                      
                      // If in standalone mode, redirect to home
                      if (standalone) {
                        window.location.href = '/';
                      }
                    } else {
                      console.log('Email verification canceled or failed, not logging in');
                    }
                  }} 
                />,
                verifyEmailRoot
              );
            } catch (renderError) {
              console.error('Error rendering verification dialog:', renderError);
              // Fallback to alert if rendering fails
              alert('Please check your email and verify your account before logging in.');
            }
            
          } catch (error) {
            console.error('Error sending verification code:', error);
            alert('An error occurred while sending verification code. Please try again later.');
          }
      } else {
        console.log("Signup failed:", response.data.message || "Unknown error");
        // Only show alert for actual failures
        if (!response.data.success) {
          alert(response.data.message || "Signup failed. Please try again.");
        }
      }
    } catch (error) {
      console.error("Signup error:", error);
      // Restore original alert behavior before showing any error
      restoreAlerts();
      alert("An error occurred during signup. Please try again.");
    } finally {
      // Make sure we always restore the original alert function
      restoreAlerts();
      setIsSubmitting(false);
    }
  };
  
  // If user wants to login instead
  const handleLoginClick = () => {
    // For dialog mode, we can toggle to login dialog
    if (!standalone) {
      setShowLogin(true);
      if (handleLogin) {
        handleLogin();
      }
    } else {
      // For standalone mode, redirect to login page
      window.location.href = '/login';
    }
  };
  
  // Show loading indicator until ready
  if (!isReady) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        padding: '50px'
      }}>
        <CircularProgress size={40} />
      </div>
    );
  }
  
  console.log('Rendering SignupDialog with auth state:', isAuthenticated);
  
  // Handle click on overlay background to close dialog
  const handleOverlayClick = (event) => {
    // Only proceed if this is a direct click on the overlay (not its children)
    if (isAccountCreation && event.target === event.currentTarget) {
      navigateToHome();
    }
  };
  
  return (
    <div 
      className={standalone ? 'standalone-page' : 'dialog-mode'} 
      style={standalone ? { 
        backgroundImage: 'linear-gradient(to right, #0f0f3d, #050539)',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px'
      } : {
        position: isAccountCreation ? 'fixed' : 'relative',
        top: isAccountCreation ? '0' : 'auto',
        left: isAccountCreation ? '0' : 'auto',
        right: isAccountCreation ? '0' : 'auto',
        bottom: isAccountCreation ? '0' : 'auto',
        zIndex: isAccountCreation ? '9999' : 'auto',
        display: isAccountCreation ? 'flex' : 'block',
        justifyContent: isAccountCreation ? 'center' : 'normal',
        alignItems: isAccountCreation ? 'center' : 'normal',
        backgroundColor: isAccountCreation ? 'rgba(0, 0, 0, 0.5)' : 'transparent',
        backdropFilter: isAccountCreation ? 'blur(5px)' : 'none'
      }}
      onClick={handleOverlayClick}
    >
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        maxWidth: isAccountCreation ? '450px' : (isMobile ? '100%' : '900px'),
        width: isAccountCreation ? 'auto' : '100%',
        justifyContent: 'space-between',
        backgroundColor: 'transparent',
        transform: isAccountCreation ? 'translateY(-40px)' : 'none' // Shift slightly up for better positioning
      }}>
        {/* Benefits Column - Only shown in standalone mode or on larger screens, and NOT in account creation mode */}
        {!isAccountCreation && (standalone || !isMobile) && (
          <div style={{
            flex: 1,
            padding: '40px',
            color: 'white',
            backgroundColor: '#0a0a35',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
            marginRight: isMobile ? 0 : '20px',
            marginBottom: isMobile ? '20px' : 0,
            order: isMobile ? 1 : 2
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '20px' }}>E3D Premium Benefits</h2>
            <div style={{ marginBottom: '30px' }}>
              <h3>Included in Your Premium Subscription:</h3>
              <ul style={{ paddingLeft: '20px', textAlign: 'left' }}>
                <li>Free NFT Mints: Get 3 free NFT mints per month (40 per year with annual subscription)</li>
                <li>50% Off Additional Mints: Enjoy half price on any additional NFT mints beyond your free allocation</li>
                <li>Full Newsletter Access: Unrestricted access to our daily newsletter with market insights and tips</li>
                <li>Like Feature: Mark your favorite tokens and create personalized watchlists</li>
                <li>Custom Portfolio Mode: Track and visualize your holdings with advanced analytics</li>
                <li>Ad-Free Experience: No banner ads or promotions throughout the platform</li>
              </ul>
            </div>
            
            <h3>Premium Pricing</h3>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '20px'
            }}>
              <div style={{
                flex: '1 0 48%',
                backgroundColor: '#141451',
                padding: '15px 10px',
                borderRadius: '5px',
                marginRight: '10px'
              }}>
                <h4 style={{ marginTop: 0 }}>Pay with Card or ETH</h4>
                <p style={{ fontSize: '20px', fontWeight: 'bold', color: '#50e3c2' }}>$20/month</p>
                <p style={{ color: '#aaa', marginBottom: 0 }}>or $200/year</p>
              </div>
              <div style={{
                flex: '1 0 48%',
                backgroundColor: '#141451',
                padding: '15px 10px',
                borderRadius: '5px'
              }}>
                <h4 style={{ marginTop: 0 }}>Pay with E3D Tokens</h4>
                <p style={{ fontSize: '20px', fontWeight: 'bold', color: '#50e3c2' }}>$10/month</p>
                <p style={{ color: '#aaa', marginBottom: 0 }}>or $100/year — 50% token-holder discount</p>
              </div>
            </div>

            {/* Use-case links */}
            <div style={{ marginTop: 28, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <p style={{ color: '#9ea4d8', fontSize: 12, marginTop: 0, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                See it in action
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <a
                  href="/story-use-cases"
                  style={{ color: '#4d79ff', textDecoration: 'none', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  See how funds use Stories →
                </a>
                <a
                  href="/thesis-use-cases"
                  style={{ color: '#4d79ff', textDecoration: 'none', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  See how funds use Theses →
                </a>
              </div>
            </div>
          </div>
          )}
        
        {/* Form Column */}
        <div style={{
          flex: 1,
          padding: isAccountCreation ? '25px' : '40px',
          backgroundColor: '#0a0a35',
          borderRadius: '12px',
          boxShadow: isAccountCreation ? '0 10px 30px rgba(0, 0, 0, 0.7)' : '0 4px 20px rgba(0, 0, 0, 0.5)',
          color: 'white',
          order: isMobile ? 2 : 1,
          border: isAccountCreation ? '1px solid rgba(255,255,255,0.1)' : 'none',
          animation: isAccountCreation ? 'fadeIn 0.3s ease-out' : 'none'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: isAccountCreation ? '15px' : '20px',
            borderBottom: isAccountCreation ? '1px solid rgba(255,255,255,0.1)' : 'none',
            paddingBottom: isAccountCreation ? '10px' : '0'
          }}>
            <h2 style={{ 
              color: 'white', 
              margin: 0, 
              fontSize: isAccountCreation ? '1.5rem' : '1.75rem',
              fontWeight: isAccountCreation ? '500' : 'bold' 
            }}>
              {isAccountCreation ? 'Create Account' : (isAuthenticated ? 'Your Premium Subscription' : 'Create Your Account')}
            </h2>
            <IconButton 
              aria-label="close" 
              onClick={navigateToHome} 
              style={{ 
                color: 'white',
                padding: isAccountCreation ? '4px' : '8px'
              }}
            >
              <FaTimes />
            </IconButton>
          </div>

          <div style={{
            marginBottom: '18px',
            padding: '15px',
            borderRadius: '8px',
            backgroundColor: 'rgba(77, 121, 255, 0.12)',
            border: '1px solid rgba(77, 121, 255, 0.45)',
          }}>
            <h3 style={{ color: '#50e3c2', marginTop: 0, marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: '8px' }}>⏱</span>
              API Rate Limit Tiers
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
              {rateLimitTiers.map((tier) => (
                <div
                  key={tier.label}
                  style={{
                    padding: '12px',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(0, 0, 0, 0.22)',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <p style={{ color: '#ffffff', margin: '0 0 6px 0', fontWeight: 'bold' }}>{tier.label}</p>
                  <p style={{ color: '#a0a0a0', margin: 0, fontSize: '0.9em' }}>{tier.dailyLimit}</p>
                  <p style={{ color: '#a0a0a0', margin: '4px 0 0 0', fontSize: '0.9em' }}>{tier.interval}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Subscription Status - Show for authenticated users */}
          {isAuthenticated && (
            <>
            <div style={{
              marginBottom: '20px',
              padding: '15px',
              borderRadius: '8px',
              backgroundColor: 'rgba(77, 121, 255, 0.15)',
              border: '1px solid #4d79ff',
              boxShadow: '0px 0px 8px rgba(77, 121, 255, 0.3)'
            }}>
              <h3 style={{ color: '#50e3c2', marginTop: 0, marginBottom: '15px', display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: '8px' }}>★</span>
                Your Current Subscription
              </h3>

              {subscriptionDetailsLoading ? (
                <div style={{ color: '#a0a0a0' }}>Loading subscription details...</div>
              ) : subscriptionDetails ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div>
                    <p style={{ color: '#a0a0a0', margin: '0 0 5px 0', fontSize: '0.9em' }}>Plan Type</p>
                    <p style={{ color: 'white', margin: '0', fontWeight: 'bold' }}>
                      {(Array.isArray(mongoUserRoles) && mongoUserRoles.includes('premium') && !subscriptionDetails.isActive)
                        ? 'Premium account active'
                        : (subscriptionDetails.isActive ? (
                          <> {subscriptionDetails.tier === 1 ? 'Monthly' : 'Annual'} Premium </>
                        ) : (
                          'No active premium subscription'
                        ))}
                    </p>
                  </div>

                  <div>
                    <p style={{ color: '#a0a0a0', margin: '0 0 5px 0', fontSize: '0.9em' }}>Payment Method</p>
                    <p style={{ color: 'white', margin: '0', fontWeight: 'bold' }}>
                      {(!subscriptionDetails.isActive ? '—' : (() => {
                        // Check various conditions for E3D payment
                        if (subscriptionDetails.paidWithE3D === true) return 'E3D Tokens';
                        if (subscriptionDetails.paymentMethod && String(subscriptionDetails.paymentMethod).toUpperCase() === 'E3D') return 'E3D Tokens';
                        
                        // Special check for known E3D subscriptions
                        // E3D annual subscriptions typically expire far in the future (2+ years)
                        const expDate = new Date(subscriptionDetails.expiration);
                        const monthsUntilExpiration = (expDate - new Date()) / (30 * 24 * 60 * 60 * 1000);
                        
                        // If annual subscription with 40 mints and expires in 20+ months, it's likely E3D
                        if (subscriptionDetails.tier === 2 && 
                            subscriptionDetails.remainingMints >= 35 && 
                            monthsUntilExpiration > 20) {
                          return 'E3D Tokens';
                        }
                        
                        // Check if expiration is specifically 9/9/2027 (known E3D payment)
                        if (expDate.getMonth() === 8 && expDate.getDate() === 9 && expDate.getFullYear() === 2027) {
                          return 'E3D Tokens';
                        }
                        
                        return 'ETH';
                      })())}
                    </p>
                  </div>

                  <div>
                    <p style={{ color: '#a0a0a0', margin: '0 0 5px 0', fontSize: '0.9em' }}>Expiration Date</p>
                    <p style={{ color: 'white', margin: '0', fontWeight: 'bold' }}>
                      {(subscriptionDetails.isActive && subscriptionDetails.expiration) ? subscriptionDetails.expiration.toLocaleDateString() : '—'}
                    </p>
                  </div>

                  <div>
                    <p style={{ color: '#a0a0a0', margin: '0 0 5px 0', fontSize: '0.9em' }}>Free Mints Remaining</p>
                    <p style={{ color: '#50e3c2', margin: '0', fontWeight: 'bold' }}>
                      {subscriptionDetails.remainingMints ?? '—'}
                    </p>
                  </div>
                </div>
              ) : subscriptionDetailsLoaded ? (
                <div style={{ color: '#a0a0a0' }}>
                  {Array.isArray(mongoUserRoles) && mongoUserRoles.includes('premium')
                    ? 'Premium account active. Subscription details are not available.'
                    : 'No subscription details found.'}
                </div>
              ) : (
                <div style={{ color: '#a0a0a0' }}>Loading subscription details...</div>
              )}
            </div>

            {(walletConnected && (effectiveUserAddress || mongoUserAddress || localStorage.getItem('userAddress'))) ? (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                borderRadius: '8px',
                backgroundColor: 'rgba(80, 227, 194, 0.08)',
                border: '1px solid rgba(80, 227, 194, 0.35)'
              }}>
                <h3 style={{ color: '#50e3c2', marginTop: 0, marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
                  <span style={{ marginRight: '8px' }}>Wallet</span>
                  <span>Wallet Subscription</span>
                </h3>
                {walletSubscriptionLoading ? (
                  <div style={{ color: '#a0a0a0' }}>Loading wallet subscription...</div>
                ) : walletSubscriptionDetails ? (
                  <div style={{ color: 'white', fontWeight: 'bold' }}>
                    {walletSubscriptionDetails.isActive ? 'Active (On-chain)' : 'Not active (On-chain)'}
                  </div>
                ) : walletSubscriptionLoaded ? (
                  <div style={{ color: '#a0a0a0' }}>No wallet subscription details found.</div>
                ) : null}
              </div>
            ) : null}
            </>
          )}
          
          <div style={{ marginBottom: '20px' }}>
            {!isAuthenticated && (
              <>
                <h3 style={{ color: 'white', marginBottom: '15px' }}>Create a Free Account</h3>

                {/* Add email/password fields when in account creation mode or for new signups */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '15px',
                  backgroundColor: '#1a1a3a',
                  padding: '15px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  animation: 'slideUp 0.4s ease-out forwards',
                  animationDelay: '0.1s',
                  opacity: 0 // Start invisible and animate in
                }}>
                  <TextField
                    label="Email Address"
                    type="email"
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={effectiveUsername}
                    onChange={(e) => {
                      // Update our local state
                      setLocalUsername(e.target.value);

                      // Also update parent component's state if available
                      if (typeof setUsername === 'function') {
                        setUsername(e.target.value);
                      }
                    }}
                    error={usernameError}
                    helperText={usernameError ? "Please enter a valid email address" : ""}
                    InputProps={{
                      style: {
                        color: 'white',
                        backgroundColor: '#0a0a35',
                        borderRadius: '6px',
                        fontSize: '0.95rem'
                      },
                    }}
                    InputLabelProps={{
                      style: { color: '#aaa' },
                      shrink: true
                    }}
                  />

                  <TextField
                    label="Password"
                    type="password"
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    InputProps={{
                      style: {
                        color: 'white',
                        backgroundColor: '#0a0a35',
                        borderRadius: '6px',
                        fontSize: '0.95rem'
                      },
                    }}
                    InputLabelProps={{
                      style: { color: '#aaa' },
                      shrink: true
                    }}
                  />

                  <TextField
                    label="Confirm Password"
                    type="password"
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    error={confirmPasswordError}
                    helperText={confirmPasswordError ? "Passwords don't match" : ""}
                    InputProps={{
                      style: {
                        color: 'white',
                        backgroundColor: '#0a0a35',
                        borderRadius: '6px',
                        fontSize: '0.95rem'
                      },
                    }}
                    InputLabelProps={{
                      style: { color: '#aaa' },
                      shrink: true
                    }}
                  />
                </div>
              </>
            )}

            <div style={{
                display: 'flex',
              flexDirection: 'column',
              gap: '15px',
              backgroundColor: '#232323',
              padding: '20px',
              borderRadius: '5px'
            }}>
              {!isAuthenticated && (
                <FormControlLabel
                  value="free"
                  control={
                    <Radio
                      checked={paymentMethod === 'free'}
                      onChange={handlePaymentMethodChange}
                      value="free"
                      color="primary"
                    />
                  }
                  label={
                    <div style={{ textAlign: 'left' }}>
                      <span style={{ color: 'white', display: 'block' }}><strong>Free Account</strong></span>
                      <span style={{ color: '#a0a0a0', fontSize: '0.9em' }}>Basic access with AI newsletter</span>
                    </div>
                  }
                />
              )}

              {/* Billing interval toggle -- applies to all three paid payment methods */}
              <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                <Button
                  variant={subscriptionInterval === 'month' ? 'contained' : 'outlined'}
                  onClick={() => setSubscriptionInterval('month')}
                  style={{ flex: 1, color: subscriptionInterval === 'month' ? '#0a0a35' : '#4fc3f7', backgroundColor: subscriptionInterval === 'month' ? '#4fc3f7' : 'transparent', borderColor: '#4fc3f7' }}
                >
                  Monthly
                </Button>
                <Button
                  variant={subscriptionInterval === 'year' ? 'contained' : 'outlined'}
                  onClick={() => setSubscriptionInterval('year')}
                  style={{ flex: 1, color: subscriptionInterval === 'year' ? '#0a0a35' : '#4fc3f7', backgroundColor: subscriptionInterval === 'year' ? '#4fc3f7' : 'transparent', borderColor: '#4fc3f7' }}
                >
                  Yearly
                </Button>
              </div>

              <FormControlLabel
                value="card"
                control={
                  <Radio
                    checked={paymentMethod === 'card'}
                    onChange={handlePaymentMethodChange}
                    value="card"
                    color="primary"
                  />
                }
                label={
                  <div style={{ textAlign: 'left' }}>
                    <span style={{ color: 'white', display: 'block' }}><strong>Pay with Card</strong></span>
                    <span style={{ color: '#a0a0a0', fontSize: '0.9em' }}>
                      Stripe checkout — {subscriptionInterval === 'year' ? '$200/year' : '$20/month'}
                    </span>
                  </div>
                }
              />

              <FormControlLabel
                value="eth"
                control={
                  <Radio
                    checked={paymentMethod === 'eth'}
                    onChange={handlePaymentMethodChange}
                    value="eth"
                    color="primary"
                  />
                }
                label={
                  <div style={{ textAlign: 'left' }}>
                    <span style={{ color: 'white', display: 'block' }}><strong>Pay with ETH</strong></span>
                    <span style={{ color: '#a0a0a0', fontSize: '0.9em' }}>
                      On-chain — {subscriptionInterval === 'year' ? '$200/year' : '$20/month'}
                    </span>
                  </div>
                }
              />

              <FormControlLabel
                value="e3d"
                control={
                  <Radio
                    checked={paymentMethod === 'e3d'}
                    onChange={handlePaymentMethodChange}
                    value="e3d"
                    color="primary"
                  />
                }
                label={
                  <div style={{ textAlign: 'left' }}>
                    <span style={{ color: 'white', display: 'block' }}><strong>Pay with E3D Tokens</strong></span>
                    <span style={{ color: '#a0a0a0', fontSize: '0.9em' }}>
                      On-chain, 50% token-holder discount — {subscriptionInterval === 'year' ? '$100/year' : '$10/month'}
                    </span>
                  </div>
                }
              />

              {/* Premium Account Requirements Banner */}
              {(paymentMethod === 'eth' || paymentMethod === 'e3d') && (
                <div style={{ 
                  marginTop: '10px', 
                  backgroundColor: '#1A1A1A',
                  padding: '15px', 
                  borderRadius: '5px', 
                  borderLeft: '3px solid #4fc3f7' 
                }}>
                  {/* Login Status Check */}
                  {!isAuthenticated ? (
                    <div>
                      <p style={{ 
                        color: 'white', 
                        margin: '0 0 10px 0',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <span style={{ color: 'orange', marginRight: '8px', fontSize: '18px' }}>⚠</span>
                        <span><strong>Account Required:</strong> Login first for premium subscription</span>
                      </p>
                      <Button 
                        variant="outlined" 
                        onClick={handleLoginClick}
                        style={{ 
                          borderColor: '#4fc3f7', 
                          color: '#4fc3f7'
                        }}
                      >
                        Login Now
                      </Button>
                    </div>
                  ) : !walletConnected ? (
                    <div>
                      <p style={{ 
                        color: 'white', 
                        margin: '0 0 10px 0',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <span style={{ color: 'orange', marginRight: '8px', fontSize: '18px' }}>⚠</span>
                        <span>Connect your wallet to enable premium subscription</span>
                      </p>
                      <Button 
                        variant="outlined" 
                        onClick={connectWallet}
                        style={{ 
                          borderColor: '#4fc3f7', 
                          color: '#4fc3f7'
                        }}
                      >
                        Connect Wallet
                      </Button>
                    </div>
                  ) : (
                    <p style={{ 
                      color: '#4fc3f7', 
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <span style={{ marginRight: '8px', fontSize: '16px' }}>✓</span>
                      All requirements met - Ready for premium subscription
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {!isAuthenticated && (
          <FormControlLabel
            control={
              <Checkbox
                checked={subscribeNewsletter}
                onChange={(e) => setSubscribeNewsletter(e.target.checked)}
                name="subscribeNewsletter"
                color="primary"
              />
            }
            style={{ color: 'white', marginBottom: '20px' }}
            label="Sign up for the daily E3D AI Newsletter"
          />
          )}
          
          {/* Transaction Status */}
          {transactionStatus && (
            <div style={{ 
              backgroundColor: '#1A1A1A', 
              padding: '15px', 
              marginTop: '15px',
              borderRadius: '5px',
              display: 'flex',
              alignItems: 'center',
              gap: '15px'
            }}>
              <CircularProgress size={24} style={{ color: '#4fc3f7' }} />
              <span style={{ color: 'white' }}>{transactionStatus}</span>
            </div>
          )}
          
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
            <Button
              variant="contained"
              color="primary"
              style={{
                backgroundColor: isAccountCreation ? '#50e3c2' : '#4d79ff',
                color: isAccountCreation ? '#0a0a35' : 'white', // Dark text on light button for better contrast
                padding: isAccountCreation ? '12px 24px' : '10px 20px',
                marginRight: '10px',
                fontWeight: 'bold',
                borderRadius: '8px',
                boxShadow: isAccountCreation ? '0 4px 12px rgba(80, 227, 194, 0.3)' : 'none',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: isAccountCreation ? '0.9rem' : 'inherit'
              }}
              disabled={
                isSubmitting ||
                (isAccountCreation && (confirmPasswordError || !effectiveUsername || !password || password !== confirmPassword)) ||
                (!isAccountCreation && (
                  (!isAuthenticated && (confirmPasswordError || usernameError)) ||
                  (paymentMethod !== 'free' && !isAuthenticated) ||
                  ((paymentMethod === 'eth' || paymentMethod === 'e3d') && !walletConnected)
                ))
              }
              title={
                isAccountCreation
                  ? "Create your free account"
                  : (paymentMethod !== 'free' && !isAuthenticated)
                    ? "You must log in first"
                    : ((paymentMethod === 'eth' || paymentMethod === 'e3d') && !walletConnected)
                      ? "Please connect your wallet"
                      : ""
              }
              onClick={handleSignupSubmit}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={16} style={{ color: 'white', marginRight: '8px' }} />
                  Processing...
                </>
              ) : isAccountCreation ? 'CREATE ACCOUNT' : (isAuthenticated ? 'UPGRADE ACCOUNT' : 'SIGN UP')}
            </Button>
            <Button
              variant={isAccountCreation ? "text" : "outlined"}
              style={{
                color: isAccountCreation ? 'rgba(255,255,255,0.7)' : 'white',
                borderColor: isAccountCreation ? 'transparent' : 'white',
                padding: isAccountCreation ? '12px 16px' : '10px 20px',
                backgroundColor: 'transparent',
                textTransform: isAccountCreation ? 'none' : 'uppercase'
              }}
              onClick={navigateToHome}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupDialog;
